import React, { useEffect, useRef } from "react";

// Ultra-optimized Interactive Particles - Zero React re-renders
// Uses direct DOM manipulation for maximum performance
const Particle = React.memo(function Particle() {
  const followerRef = useRef(null);
  const trailRefs = useRef([]);
  const rafRef = useRef(null);
  const targetPos = useRef({ x: -100, y: -100 });
  const currentPos = useRef({ x: -100, y: -100 });
  const trailPositions = useRef([
    { x: -100, y: -100 },
    { x: -100, y: -100 },
    { x: -100, y: -100 },
    { x: -100, y: -100 },
    { x: -100, y: -100 },
  ]);
  const isActiveRef = useRef(false);

  useEffect(() => {
    const follower = followerRef.current;
    const trails = trailRefs.current;

    // Smooth animation loop - no React state updates!
    const animate = () => {
      const lerp = 0.1; // Main follower speed
      const trailLerp = 0.15; // Trail speed (slightly faster to catch up)
      
      // Update main follower position
      currentPos.current.x += (targetPos.current.x - currentPos.current.x) * lerp;
      currentPos.current.y += (targetPos.current.y - currentPos.current.y) * lerp;
      
      // Update trail positions (each follows the previous)
      for (let i = 0; i < trailPositions.current.length; i++) {
        const target = i === 0 ? currentPos.current : trailPositions.current[i - 1];
        trailPositions.current[i].x += (target.x - trailPositions.current[i].x) * trailLerp;
        trailPositions.current[i].y += (target.y - trailPositions.current[i].y) * trailLerp;
      }
      
      // Direct DOM updates - no React involved!
      if (follower) {
        follower.style.transform = `translate(${currentPos.current.x}px, ${currentPos.current.y}px)`;
      }
      
      trails.forEach((trail, i) => {
        if (trail) {
          trail.style.transform = `translate(${trailPositions.current[i].x}px, ${trailPositions.current[i].y}px)`;
        }
      });
      
      rafRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      targetPos.current = { x: e.clientX, y: e.clientY };
      
      if (!isActiveRef.current) {
        isActiveRef.current = true;
        follower?.classList.add('active');
        trails.forEach(trail => trail?.classList.add('active'));
      }
    };

    const handleMouseLeave = () => {
      isActiveRef.current = false;
      targetPos.current = { x: -100, y: -100 };
      follower?.classList.remove('active');
      trails.forEach(trail => trail?.classList.remove('active'));
    };

    // Start animation loop
    rafRef.current = requestAnimationFrame(animate);
    
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const setTrailRef = (index) => (el) => {
    trailRefs.current[index] = el;
  };

  return (
    <div className="css-particles-container">
      {/* Mouse follower - main glow */}
      <div ref={followerRef} className="mouse-follower">
        <div className="mouse-follower-core"></div>
        <div className="mouse-follower-glow"></div>
        <div className="mouse-follower-ring mouse-follower-ring-1"></div>
        <div className="mouse-follower-ring mouse-follower-ring-2"></div>
        <div className="mouse-follower-ring mouse-follower-ring-3"></div>
      </div>

      {/* Trailing particles */}
      <div ref={setTrailRef(0)} className="mouse-trail mouse-trail-1" />
      <div ref={setTrailRef(1)} className="mouse-trail mouse-trail-2" />
      <div ref={setTrailRef(2)} className="mouse-trail mouse-trail-3" />
      <div ref={setTrailRef(3)} className="mouse-trail mouse-trail-4" />
      <div ref={setTrailRef(4)} className="mouse-trail mouse-trail-5" />

      {/* Floating ambient particles */}
      <div className="css-particle css-particle-1"></div>
      <div className="css-particle css-particle-2"></div>
      <div className="css-particle css-particle-3"></div>
      <div className="css-particle css-particle-4"></div>
      <div className="css-particle css-particle-5"></div>
      <div className="css-particle css-particle-6"></div>
      <div className="css-particle css-particle-7"></div>
      <div className="css-particle css-particle-8"></div>
      <div className="css-particle css-particle-9"></div>
      <div className="css-particle css-particle-10"></div>
      <div className="css-particle css-particle-11"></div>
      <div className="css-particle css-particle-12"></div>
      <div className="css-particle css-particle-13"></div>
      <div className="css-particle css-particle-14"></div>
      <div className="css-particle css-particle-15"></div>
      
      {/* Glowing orbs */}
      <div className="css-particle-orb css-particle-orb-1"></div>
      <div className="css-particle-orb css-particle-orb-2"></div>
      <div className="css-particle-orb css-particle-orb-3"></div>
      
      {/* Connection lines */}
      <div className="css-particle-line css-particle-line-1"></div>
      <div className="css-particle-line css-particle-line-2"></div>
      <div className="css-particle-line css-particle-line-3"></div>
      <div className="css-particle-line css-particle-line-4"></div>
      <div className="css-particle-line css-particle-line-5"></div>
      
      {/* Gradient mesh background */}
      <div className="css-particle-mesh"></div>
    </div>
  );
});

export default Particle;
